import { expiryDateToMillis } from '../hostedReport.js';
import type {
  PortalAudienceReach,
  PortalAudienceRule,
  PortalLibrarySection,
  PortalLibraryStaffRow,
  PortalLibraryTileState,
} from '../../types/portalLibrary.js';
import { PORTAL_LIBRARY_SECTION_LABELS } from '../../types/portalLibrary.js';
import { strategySectionAudienceLabel } from '../portalStrategyNav.js';
import { categoryError, defaultVisibleUntil, normaliseTags, periodError } from './entryRules.js';
import { formatDateTime } from '../hostedReportsView.js';

// CLIENT PORTAL LIBRARY — the STAFF surfaces' pure view logic, so every
// sentence and every payload is asserted without jsdom.

/**
 * ⚠️ DIFFERENT WORDS BECAUSE THEY NEED DIFFERENT FIXES (brief §3c). Revoked is
 * a staff action on the FILE and is not time-based; ended is the tile's own
 * clock; a missing file was deleted or purged. Collapsing any two of these
 * would send someone to fix the wrong thing.
 */
export const TILE_STATE_DISPLAY: Record<
  PortalLibraryTileState,
  { label: string; variant: 'success' | 'info' | 'neutral' | 'warning' | 'danger'; help: string }
> = {
  live: { label: 'Live', variant: 'success', help: 'Clients in the audience can see this now.' },
  scheduled: {
    label: 'Scheduled',
    variant: 'info',
    help: 'Not shown yet — it appears on its start date.',
  },
  ended: {
    label: 'Ended',
    variant: 'neutral',
    help: 'Past its end date, so clients no longer see it. Move the end date to bring it back.',
  },
  reportRevoked: {
    label: 'File revoked',
    variant: 'danger',
    help: 'The hosted report behind this tile was revoked, so clients no longer see the tile. Revoking cannot be undone — publish the file again to replace it.',
  },
  reportExpired: {
    label: 'File expired',
    variant: 'warning',
    help: 'The tile is inside its dates but its file has expired — the two dates have drifted apart. Save the tile’s end date again to bring them back in step.',
  },
  reportMissing: {
    label: 'File deleted',
    variant: 'danger',
    help: 'The hosted report behind this tile no longer exists, so clients no longer see it.',
  },
};

/** "This will appear for 12 accounts across 2 brands." */
export function reachSentence(reach: PortalAudienceReach): string {
  const n = reach.accountCount;
  const m = reach.brandCount;
  const accounts = `${n} account${n === 1 ? '' : 's'}`;
  if (m === 0) return `This will appear for ${accounts}.`;
  return `This will appear for ${accounts} across ${m} brand${m === 1 ? '' : 's'}.`;
}

/**
 * The second number: "7 of them have portal access today."
 *
 * ⚠️ Stated, never folded into the first — N is who the RULES reach, M is who can
 * actually open it now, and a publisher needs both: an `everyone` tile with M = 3
 * is a very different blast radius from one with M = 300. Zero is SAID, because
 * a tile nobody can open yet is legitimate (it reaches them the day they are
 * invited) but should never be a surprise.
 */
export function portalAccessSentence(reach: PortalAudienceReach): string {
  const m = reach.accountsWithPortalAccess;
  const n = reach.accountCount;
  // ⚠️⚠️ ROUND 6: THE NUMBER CHANGED MEANING, SO THE WORDS DID TOO. M is now the
  // matched accounts that can SEE THE SECTION *and* have portal access — two
  // conditions — and the sentence names both. "7 of them have portal access"
  // would describe a different, larger number whenever the section excludes
  // some of the matched accounts. "Them" is the N matched accounts, as in the
  // reach sentence before it.
  //
  // Zero section-seers is the refusal, shown instead of this line.
  if (n === 0 || reach.accountsSeeingSection === 0) return '';
  if (m === 0) {
    return 'None of the accounts that can see this section has portal access yet, so nobody will see it until someone is invited.';
  }
  if (m === n) {
    return n === 1
      ? 'It can see this section and has portal access today.'
      : `All ${m} can see this section and have portal access today.`;
  }
  return `${m} of them can see this section and ${m === 1 ? 'has' : 'have'} portal access today.`;
}

/**
 * ⚠️ ROUND 6 §3 — "only K of them can see Monthly campaigns". Said only when the
 * section loses some of the matched accounts; ALL of them is silent (nothing to
 * report) and NONE is the refusal (`reachRefusal`), not a sentence.
 */
export function sectionReachSentence(reach: PortalAudienceReach): string | null {
  const k = reach.accountsSeeingSection;
  if (reach.accountCount === 0 || k === 0 || k === reach.accountCount) return null;
  const label = PORTAL_LIBRARY_SECTION_LABELS[reach.section];
  const who = strategySectionAudienceLabel(reach.section);
  return `Only ${k} of them can see ${label}${who ? `, which is shown to ${who}` : ''}.`;
}

/** Under the section picker: who a strategy section is shown to. Null for the rest. */
export function sectionAudienceHint(section: PortalLibrarySection): string | null {
  const who = strategySectionAudienceLabel(section);
  return who ? `Shown to ${who}.` : null;
}

/** The rules in the audience that match nothing — worth saying even when others do. */
export function deadRuleLabels(reach: PortalAudienceReach): string[] {
  return reach.perRule.filter((r) => r.accountCount === 0).map((r) => r.label);
}

// ---------- the "Add to Kyck portal" fields on the upload form ----------

export type PortalPublishFields = {
  section: PortalLibrarySection;
  summary: string;
  audience: PortalAudienceRule[];
  tags: string[];
  /** `yyyy-mm`, monthly only. */
  period: string;
  /** A category id, college only; '' = none chosen yet. */
  categoryId: string;
  /** `yyyy-mm-dd`; '' = from now. */
  visibleFromDate: string;
  /** `yyyy-mm-dd`, ignored while `neverEnds` is ticked. */
  visibleUntilDate: string;
  neverEnds: boolean;
};

/**
 * Defaults for a section. ⚠️ The end date follows the SECTION's default
 * (never / 90 days / never, brief §3b), so choosing a section resets it.
 */
export function portalFieldsForSection(
  section: PortalLibrarySection,
  nowMs: number,
  keep?: PortalPublishFields,
): PortalPublishFields {
  const until = defaultVisibleUntil(section, nowMs);
  return {
    section,
    summary: keep?.summary ?? '',
    audience: keep?.audience ?? [],
    tags: keep?.tags ?? [],
    period: keep?.period ?? '',
    categoryId: keep?.categoryId ?? '',
    visibleFromDate: keep?.visibleFromDate ?? '',
    // A hidden date still needs a sensible value for when "never" is unticked.
    visibleUntilDate: until ?? keep?.visibleUntilDate ?? '',
    neverEnds: until === null,
  };
}

export type PortalPublishPayload = {
  section: PortalLibrarySection;
  summary: string | null;
  audience: PortalAudienceRule[];
  tags: string[];
  period: string | null;
  /** Round 4 §6: required for college, null elsewhere. */
  categoryId: string | null;
  visibleFromMs: number | null;
  /** ⚠️ Also the hosted report's expiry — one date, brief §3c. */
  visibleUntilMs: number | null;
};

/**
 * Form → payload, or the first reason it cannot be sent. The SERVER re-checks
 * every one of these; this is so the button can say why before the upload.
 */
export function buildPortalPublishPayload(
  f: PortalPublishFields,
): { ok: true; payload: PortalPublishPayload } | { ok: false; message: string } {
  if (f.audience.length === 0) {
    return { ok: false, message: 'Choose who this is for.' };
  }
  const period = f.section === 'strategy.monthly' ? f.period.trim() || null : null;
  const periodErr = periodError(f.section, period);
  if (periodErr) return { ok: false, message: periodErr };
  const categoryId = f.section === 'college' ? f.categoryId.trim() || null : null;
  const categoryErr = categoryError(f.section, categoryId);
  if (categoryErr) return { ok: false, message: categoryErr };

  let visibleFromMs: number | null = null;
  if (f.visibleFromDate) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(f.visibleFromDate);
    if (!m) return { ok: false, message: 'Pick a valid start date.' };
    // The START of that local day — a tile "from 3 October" is up all of it.
    visibleFromMs = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  }

  let visibleUntilMs: number | null = null;
  if (!f.neverEnds) {
    visibleUntilMs = expiryDateToMillis(f.visibleUntilDate);
    if (visibleUntilMs === null) return { ok: false, message: 'Pick a valid end date.' };
  }

  return {
    ok: true,
    payload: {
      section: f.section,
      summary: f.summary.trim() || null,
      audience: f.audience,
      tags: normaliseTags(f.tags),
      period,
      categoryId,
      visibleFromMs,
      visibleUntilMs,
    },
  };
}

// ---------- adding an EXISTING report to the portal (Round 2c) ----------

// ⚠️ There is deliberately NO client-side "can this be adopted" check. The
// server's refusal is the authority (revoked / live tile / password), and a
// client check keyed on the marker wrongly blocked a stranded one.

/**
 * ⚠️⚠️ WHAT PUBLISHING WILL DO TO THE LINK, said before the click. Publishing
 * sets the link's expiry to the tile's end date — one date, not two (brief
 * §3c) — so an existing link can come BACK to life, stay alive longer, or stop
 * earlier than its owner set. Each of those is a change to a link a client may
 * already hold, so each is stated; only "no change" is silent.
 */
export function adoptLinkNote(
  reportExpiresAt: string | null,
  visibleUntilMs: number | null,
  nowMs: number,
): string | null {
  const currentMs = reportExpiresAt === null ? null : Date.parse(reportExpiresAt);
  if (currentMs === visibleUntilMs) return null;
  const until =
    visibleUntilMs === null
      ? 'the tile is removed'
      : formatDateTime(new Date(visibleUntilMs).toISOString());
  if (currentMs !== null && currentMs <= nowMs) {
    return `This link expired on ${formatDateTime(reportExpiresAt)} — adding it will make it work again until ${until}.`;
  }
  if (currentMs === null) {
    return `This link currently never expires — once added, it stops working on ${until}.`;
  }
  if (visibleUntilMs === null || visibleUntilMs > currentMs) {
    return `This link was due to expire on ${formatDateTime(reportExpiresAt)} — adding it will keep it working until ${until}.`;
  }
  return `This link was due to expire on ${formatDateTime(reportExpiresAt)} — once added, it stops working earlier, on ${until}.`;
}

/**
 * ⚠️ THE PORTAL MARKER ON THE HOSTED-REPORTS LIST (Round 4 §3). A report whose
 * marker points at a tile that no longer exists believes it is in the portal
 * when it is not — a half-failed tile delete leaves exactly that, by design
 * (tile first, then the marker). Nothing surfaced it, so it gets its OWN state.
 *
 * `entryIds` null = the library list has not loaded (or failed): the plain
 * "In portal" is shown, since claiming "tile missing" on no evidence would send
 * someone to re-publish a document that is fine. The fix for a missing tile is
 * "Add to portal", which already treats a stranded marker as free.
 */
export function portalMarkerBadge(
  marker: string | null,
  entryIds: ReadonlySet<string> | null,
): { label: string; variant: 'neutral' | 'warning'; help: string } | null {
  if (marker === null) return null;
  if (entryIds === null || entryIds.has(marker)) {
    return { label: 'In portal', variant: 'neutral', help: 'This report is a tile on the Kyck portal.' };
  }
  return {
    label: 'In portal · tile missing',
    variant: 'warning',
    help: 'This report thinks it is on the Kyck portal, but its tile no longer exists. Use “Remove from portal” to clear it — then “Add to portal” if you want to publish it again.',
  };
}

/**
 * ⚠️⚠️ ROUND 6 §1 — THE ROW'S ONE PORTAL BUTTON, chosen by the marker alone.
 *
 * No pointer → "Add to portal"; a pointer → "Remove from portal", which deletes
 * the tile if it exists and clears the marker (the stranded-marker repair).
 *
 * ⚠️ THIS IS NOT THE STATE-AS-LABEL THE ROUND 2c GUARD FORBIDS. "In portal" on a
 * button would be STATE masquerading as an action, and that is still forbidden —
 * the state stays on the badge (`portalMarkerBadge`). "Remove from portal" names
 * what the click will DO, which is what a button is for.
 *
 * ⚠️ Keyed on the marker, NOT on whether the tile was found: a stranded marker
 * needs "Remove" to repair it, and a library list that failed to load must not
 * flip a live tile's row to "Add".
 */
export function portalRowAction(marker: string | null): {
  action: 'add' | 'remove';
  label: string;
} {
  return marker === null
    ? { action: 'add', label: 'Add to portal' }
    : { action: 'remove', label: 'Remove from portal' };
}

/**
 * What the "Remove from portal" confirmation says (Round 6 §1).
 *
 * ⚠️ It NAMES THE SECTION and HOW MANY ACCOUNTS SEE IT, from the row's own
 * `reach` — the `audienceReach` the staff list already computed, never a second
 * count. "See it" is the accounts that can reach it today: matched, with the
 * section in their nav, and with portal access.
 *
 * `row` null = the marker is stranded, or the library list is not loaded; the
 * copy then says what is actually known rather than inventing a count.
 */
export function removeFromPortalSummary(
  row: PortalLibraryStaffRow | null,
  libraryLoaded: boolean,
): string {
  if (row === null) {
    return libraryLoaded
      ? 'Its tile no longer exists, so no client sees it. This only clears the report’s portal marker.'
      : 'The tile will be deleted and the report’s portal marker cleared.';
  }
  const label = PORTAL_LIBRARY_SECTION_LABELS[row.entry.section];
  const n = row.reach.accountsWithPortalAccess;
  const seeing =
    row.tileState !== 'live'
      ? 'It is not currently showing to anyone.'
      : n === 0
        ? 'No account with portal access sees it today.'
        : `${n} account${n === 1 ? '' : 's'} with portal access ${n === 1 ? 'sees' : 'see'} it today.`;
  return `This takes it off ${label} in the Kyck portal. ${seeing} The report and its shareable link are kept.`;
}

/**
 * ⚠️⚠️ ONE DATE, BOTH DOORS (Round 4 §1). A portal document KEEPS its shareable
 * link: Scott emails strategy decks and college guides as well as publishing
 * them. Said on both doors — the upload form's portal section and the adopt
 * dialog — because the consequence is not obvious: a link cannot be taken back
 * from one person, so the date and revoking are the only ways to stop it.
 */
export const PORTAL_SHARED_LINK_NOTE =
  'Its shareable link stays live alongside the tile — anyone you send it to can open it without signing in, and it can’t be taken back from just one person. The link follows this end date, so the tile and the link come down together; revoking the report in HTML hosting shuts both at once.';

/**
 * The password line in the adopt dialog (Round 4 amendment). ⚠️ NOT a blocker
 * any more: the tick is OPTIONAL. The portal checks who a client is and never
 * asks for the password; the password applies only to the shareable link, and
 * since that link is now permanent it is the only control it has — so keeping
 * it is the default, and removing it is a choice somebody makes on purpose.
 */
export function adoptPasswordNote(clearPassword: boolean): string {
  return clearPassword
    ? 'The password will be removed, so anyone holding the shareable link can open it without one. Clients in the portal are unaffected.'
    : 'The password stays on the shareable link. Clients in the portal don’t need it — the portal checks who they are.';
}

// ---------- editing a published tile ----------

export type EntryEditForm = {
  title: string;
  summary: string;
  audience: PortalAudienceRule[];
  tags: string[];
  period: string;
  categoryId: string;
  visibleFromDate: string;
  visibleUntilDate: string;
  neverEnds: boolean;
};

function localDate(iso: string): string {
  const d = new Date(Date.parse(iso));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The form pre-filled from a tile — what "unchanged" looks like. */
export function formFromEntry(
  entry: {
    title: string;
    summary: string | null;
    audience: PortalAudienceRule[];
    tags: string[];
    period: string | null;
    categoryId: string | null;
    visibleFrom: string;
    visibleUntil: string | null;
    section: PortalLibrarySection;
  },
  nowMs: number = Date.now(),
): EntryEditForm {
  return {
    title: entry.title,
    summary: entry.summary ?? '',
    audience: entry.audience,
    tags: entry.tags,
    period: entry.period ?? '',
    categoryId: entry.categoryId ?? '',
    visibleFromDate: localDate(entry.visibleFrom),
    visibleUntilDate:
      entry.visibleUntil === null
        ? (defaultVisibleUntil(entry.section, nowMs) ?? localDate(new Date(nowMs).toISOString()))
        : localDate(entry.visibleUntil),
    neverEnds: entry.visibleUntil === null,
  };
}

export type EntryPatch = {
  title?: string;
  summary?: string | null;
  audience?: PortalAudienceRule[];
  tags?: string[];
  period?: string | null;
  categoryId?: string | null;
  visibleFromMs?: number;
  visibleUntil?: { action: 'set'; atMs: number } | { action: 'never' };
};

/**
 * Only the fields that differ. ⚠️ The pre-filled form round-trips to an EMPTY
 * patch, so opening the dialog and pressing save writes no edit — and, since a
 * `visibleUntil` change also moves the hosted report's expiry, it must never
 * emit one by accident.
 */
export function buildEntryPatch(
  entry: Parameters<typeof formFromEntry>[0],
  form: EntryEditForm,
): EntryPatch {
  const patch: EntryPatch = {};
  const title = form.title.trim();
  if (title && title !== entry.title) patch.title = title;
  const summary = form.summary.trim() || null;
  if (summary !== (entry.summary ?? null)) patch.summary = summary;
  if (JSON.stringify(form.audience) !== JSON.stringify(entry.audience)) {
    patch.audience = form.audience;
  }
  const tags = normaliseTags(form.tags);
  if (JSON.stringify(tags) !== JSON.stringify(entry.tags)) patch.tags = tags;
  if (entry.section === 'strategy.monthly') {
    const period = form.period.trim() || null;
    if (period !== entry.period) patch.period = period;
  }
  if (entry.section === 'college') {
    const categoryId = form.categoryId.trim() || null;
    if (categoryId !== entry.categoryId) patch.categoryId = categoryId;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(form.visibleFromDate);
  if (m && form.visibleFromDate !== localDate(entry.visibleFrom)) {
    patch.visibleFromMs = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  }
  const currentUntil = entry.visibleUntil === null ? null : Date.parse(entry.visibleUntil);
  if (form.neverEnds) {
    if (currentUntil !== null) patch.visibleUntil = { action: 'never' };
  } else {
    const atMs = expiryDateToMillis(form.visibleUntilDate);
    if (atMs !== null && atMs !== currentUntil) patch.visibleUntil = { action: 'set', atMs };
  }
  return patch;
}
